import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import NmcBadge from "./NmcBadge";
import { Pill, Download, Stethoscope, FlaskConical, CalendarDays, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * PrescriptionView — read-only prescription card shown on the patient slip page.
 * Fetches /public/prescription/{appointmentId}. When no prescription exists,
 * renders nothing (returns null).
 *
 * The user can "Download as image" — we serialise the rendered card via
 * html2canvas (lazy-loaded to keep the initial bundle small).
 */
export default function PrescriptionView({ appointmentId }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/prescription/${appointmentId}`);
      setData(data);
    } catch { /* no prescription -> stay null */ }
  };

  useEffect(() => {
    if (!appointmentId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement("a");
      link.download = `upchaar-prescription-${appointmentId.slice(0, 8)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Downloaded prescription image");
    } catch (e) {
      toast.error(e.message || "Could not download");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return null;
  if (data.available === false) return null;

  const s = data.prescription_v2 || {};
  const patient = data.patient || {};
  const doctor = data.doctor || {};
  const org = data.org || {};

  return (
    <Card className="medical-card p-5 sm:p-6" data-testid="slip-prescription-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Pill className="w-4 h-4 text-emerald-600" />
          <h2 className="font-heading text-lg font-semibold text-slate-900">Digital Prescription</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            data-testid="slip-toggle-prescription"
          >
            {expanded ? "Hide" : "View"}
          </Button>
          {expanded && (
            <Button
              size="sm"
              onClick={download}
              disabled={downloading}
              className="bg-sky-500 hover:bg-sky-600 text-white gap-2"
              data-testid="slip-download-prescription"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {downloading ? "Downloading…" : "Download"}
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div
          ref={cardRef}
          className="mt-4 rounded-xl border border-slate-200 bg-white p-5 space-y-4"
          data-testid="slip-prescription-body"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Prescription</div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-xl font-bold text-slate-900">Dr. {doctor.name}</h3>
                <NmcBadge doctor={doctor} testid="rx-nmc-badge" />
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {doctor.specialization_en}{doctor.nmc_number ? ` · NMC ${doctor.nmc_number}` : ""}
              </div>
              {org?.name && <div className="text-xs text-slate-500">🏥 {org.name}{org.city ? `, ${org.city}` : ""}</div>}
            </div>
            <div className="text-right text-xs text-slate-500">
              <div>Patient: <span className="font-semibold text-slate-800">{patient.name}</span></div>
              <div>Token: <span className="font-mono">#{patient.ticket_number}</span></div>
              <div>{data.prescription_updated_at ? new Date(data.prescription_updated_at).toLocaleString() : ""}</div>
            </div>
          </div>

          {/* Medicines */}
          {Array.isArray(s.medicines) && s.medicines.length > 0 && (
            <section>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-2">
                <Pill className="w-3.5 h-3.5" /> Rx
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs">
                    <tr>
                      <th className="text-left py-1.5 px-2 font-medium">Medicine</th>
                      <th className="text-left py-1.5 px-2 font-medium">Dose</th>
                      <th className="text-left py-1.5 px-2 font-medium">Freq</th>
                      <th className="text-left py-1.5 px-2 font-medium">Days</th>
                      <th className="text-left py-1.5 px-2 font-medium">Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.medicines.map((m, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`rx-med-${i}`}>
                        <td className="py-2 px-2 font-semibold text-slate-800">{m.name}</td>
                        <td className="py-2 px-2 text-slate-600">{m.dose || "—"}</td>
                        <td className="py-2 px-2 font-mono">{m.frequency || "—"}</td>
                        <td className="py-2 px-2">{m.duration_days ?? "—"}</td>
                        <td className="py-2 px-2 text-slate-600">
                          {[
                            (m.instructions || "").replace("_", " "),
                            m.instructions_text || "",
                          ].filter(Boolean).join(" · ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Diagnosis / tests / instructions */}
          <div className="grid md:grid-cols-2 gap-4">
            {s.diagnosis && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1"><Stethoscope className="w-3.5 h-3.5" /> Diagnosis</div>
                <p className="text-sm text-slate-800" data-testid="rx-diagnosis">{s.diagnosis}</p>
              </div>
            )}
            {Array.isArray(s.tests_advised) && s.tests_advised.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1"><FlaskConical className="w-3.5 h-3.5" /> Tests advised</div>
                <div className="flex flex-wrap gap-1.5" data-testid="rx-tests">
                  {s.tests_advised.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}
                </div>
              </div>
            )}
            {s.special_instructions && (
              <div className="md:col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1"><FileText className="w-3.5 h-3.5" /> Special instructions</div>
                <p className="text-sm text-slate-800" data-testid="rx-instructions">{s.special_instructions}</p>
              </div>
            )}
            {s.next_visit_date && (
              <div className="md:col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1"><CalendarDays className="w-3.5 h-3.5" /> Next visit</div>
                <p className="text-sm font-semibold text-emerald-700" data-testid="rx-next-visit">{s.next_visit_date}</p>
              </div>
            )}
          </div>

          {/* Fallback plain text (only when no v2 present) */}
          {!s.medicines?.length && data.prescription_text && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Prescription</div>
              <pre className="whitespace-pre-wrap text-sm text-slate-800 font-mono">{data.prescription_text}</pre>
            </div>
          )}

          <p className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
            Powered by UpchaarQ App · Not a substitute for professional medical advice
          </p>
        </div>
      )}
    </Card>
  );
}
